import { z } from 'zod/v4';
import {
  activeAlertsPanelDefinitionSchema,
  // Panels
  barChartDefinitionSchema,
  changeEventsDataQueryDefinitionSchema,
  distributionChartDefinitionSchema,
  externalPanelDefinitionSchema,
  gaugeChartDefinitionSchema,
  graphiteTimeSeriesQueryDefinitionSchema,
  heatmapDefinitionSchema,
  loggingQueryDefinitionSchema,
  logsFieldValuesVariableDefinitionSchema,
  markdownPanelDefinitionSchema,
  monitorQueryDefinitionSchema,
  nodeGraphDefinitionSchema,
  pieChartDefinitionSchema,
  prometheusEventQueryDefinitionSchema,
  // Variables
  prometheusLabelNamesVariableDefinitionSchema,
  prometheusLabelValuesVariableDefinitionSchema,
  prometheusPromQLVariableDefinitionSchema,
  // DataQuery
  prometheusTimeSeriesQueryDefinitionSchema,
  serviceTopologyPanelDefintionSchema,
  statChartDefinitionSchema,
  staticListVariableDefinitionSchema,
  statusHistoryPanelDefinitionSchema,
  tableChartDefinitionSchema,
  // Events
  timelineEventQueryDefinitionSchema,
  timeseriesChartDefinitionSchema,
  traceQueryDefinitionSchema,
} from './plugins';
import { createDashboardDefinitionSchema, mockDataQueryDefinitionSchema } from './utils';
import { lintSchema } from './utils/lint-schema';

export const chronospherePanelDefinitionSchema = z.discriminatedUnion('kind', [
  activeAlertsPanelDefinitionSchema,
  barChartDefinitionSchema,
  distributionChartDefinitionSchema,
  externalPanelDefinitionSchema,
  gaugeChartDefinitionSchema,
  heatmapDefinitionSchema,
  timeseriesChartDefinitionSchema,
  tableChartDefinitionSchema,
  markdownPanelDefinitionSchema,
  nodeGraphDefinitionSchema,
  serviceTopologyPanelDefintionSchema,
  statChartDefinitionSchema,
  pieChartDefinitionSchema,
  statusHistoryPanelDefinitionSchema,
]);

export const chronosphereDataQueryDefinitionSchema = z.discriminatedUnion('kind', [
  changeEventsDataQueryDefinitionSchema,
  loggingQueryDefinitionSchema,
  prometheusTimeSeriesQueryDefinitionSchema,
  graphiteTimeSeriesQueryDefinitionSchema,
  mockDataQueryDefinitionSchema,
  traceQueryDefinitionSchema,
  monitorQueryDefinitionSchema,
]);

const chronosphereEventQueryDefinitionSchema = z.discriminatedUnion('kind', [
  prometheusEventQueryDefinitionSchema,
  timelineEventQueryDefinitionSchema,
]);

const chronosphereVariableDefinitionSchema = z.discriminatedUnion('kind', [
  logsFieldValuesVariableDefinitionSchema,
  prometheusLabelNamesVariableDefinitionSchema,
  prometheusLabelValuesVariableDefinitionSchema,
  prometheusPromQLVariableDefinitionSchema,
  staticListVariableDefinitionSchema,
]);

/**
 * The Zod schema for a Chronosphere dashboard.
 */
export const chronosphereDashboardSchema = createDashboardDefinitionSchema({
  panelPluginDefinitionSchema: chronospherePanelDefinitionSchema,
  dataQueryPluginDefinitionSchema: chronosphereDataQueryDefinitionSchema,
  eventPluginDefinitionSchema: chronosphereEventQueryDefinitionSchema,
  listVariablePluginDefinitionSchema: chronosphereVariableDefinitionSchema,
});

// Lint schema in development
if (process.env.NODE_ENV !== 'production') {
  lintSchema(chronosphereDashboardSchema);
}

export interface ChronosphereDashboardSchema extends z.TypeOf<typeof chronosphereDashboardSchema> {}

export type ChronospherePanelDefinition = ChronosphereDashboardSchema['spec']['panels']['0'];

export type ChronosphereVariableDefinition = ChronosphereDashboardSchema['spec']['variables']['0'];

export type ChronospherePanelQuery = NonNullable<ChronospherePanelDefinition['spec']['queries']>[0];

export type ChronosphereDataQueryDefinition = NonNullable<
  ChronospherePanelDefinition['spec']['queries']
>[0]['spec']['plugin'];
